import { ReactElement, MouseEventHandler, useCallback, useMemo } from 'react';
import { ListItemButton } from '@mui/material';

import { useAccessFeatureModalContext } from '~contexts/AccessFeatureModalContext';
import { useSubscriptionFeatures, useAppSelector } from '~hooks/index';
import { selectAuth } from '~store/slices/auth';
import { isAdminUser } from '~utils/user';

import AccessFeatureNextPlanChip from '../NextPlanChip';

import { AccessFeatureListItemButtonProps } from './types';

const AccessFeatureListItemButton = ({
  validFeature,
  availableToDate,
  onClick,
  children,
  planChipProps,
  disabled: disabledProps,
  ...rest
}: AccessFeatureListItemButtonProps): ReactElement => {
  const { handleOpen: handleOpenAccesFeatureModal } =
    useAccessFeatureModalContext();
  const { hasAccessToFeature } = useSubscriptionFeatures();
  const { user } = useAppSelector(selectAuth);

  const isValidFeature = useMemo(
    () => hasAccessToFeature(validFeature, availableToDate),
    [validFeature, hasAccessToFeature, availableToDate],
  );

  const isAdminCurrentUser = useMemo(() => isAdminUser(user), [user]);

  const disabled = useMemo(() => {
    if (!isAdminCurrentUser) {
      return !isValidFeature;
    }

    return disabledProps;
  }, [isAdminCurrentUser, disabledProps, isValidFeature]);

  const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(
    (event) => {
      event.preventDefault();

      if (isValidFeature) {
        onClick?.(event);

        return;
      }

      if (isAdminCurrentUser) {
        handleOpenAccesFeatureModal({
          validFeature,
          validateAvailableRoutesState: false,
        });

        return;
      }
    },
    [
      isValidFeature,
      isAdminCurrentUser,
      handleOpenAccesFeatureModal,
      validFeature,
      onClick,
    ],
  );

  return (
    <ListItemButton {...rest} onClick={handleClick} disabled={disabled}>
      {children}

      <AccessFeatureNextPlanChip
        {...planChipProps}
        validFeature={validFeature}
        availableToDate={availableToDate}
      />
    </ListItemButton>
  );
};

export default AccessFeatureListItemButton;
